import React, { useEffect, useRef, useState } from 'react';
import { init, dispose, registerIndicator } from 'klinecharts';
import { getTechnicalKlines } from '../api';

// ── Pivot detection (shared by Zigzag, RSI divergence, patterns) ────────────
// A swing high at i = highest high within ±lookback; swing low = lowest low.
// Consecutive same-type pivots are collapsed to the more extreme one so the
// sequence strictly alternates H, L, H, L…
function computePivots(list, lookback = 5, sel = 'price') {
  const n = list.length;
  const hi = (i) => (sel === 'price' ? list[i].high : list[i]);
  const lo = (i) => (sel === 'price' ? list[i].low : list[i]);
  const isHigh = (i) => { for (let j = Math.max(0, i - lookback); j <= Math.min(n - 1, i + lookback); j++) if (hi(j) > hi(i)) return false; return true; };
  const isLow = (i) => { for (let j = Math.max(0, i - lookback); j <= Math.min(n - 1, i + lookback); j++) if (lo(j) < lo(i)) return false; return true; };
  const raw = [];
  for (let i = lookback; i < n - lookback; i++) {
    if (isHigh(i)) raw.push({ i, type: 'H', price: hi(i) });
    else if (isLow(i)) raw.push({ i, type: 'L', price: lo(i) });
  }
  const out = [];
  for (const p of raw) {
    const last = out[out.length - 1];
    if (!last || last.type !== p.type) out.push(p);
    else if ((p.type === 'H' && p.price > last.price) || (p.type === 'L' && p.price < last.price)) out[out.length - 1] = p;
  }
  return out;
}

// ── Zigzag indicator: connects the swing pivots and labels higher-low /
// lower-high structure. Non-interactive (draw callback), so it never blocks
// zoom.
let zigzagRegistered = false;
function ensureZigzag() {
  if (zigzagRegistered) return;
  registerIndicator({
    name: 'ZIGZAG',
    shortName: 'Zigzag',
    calcParams: [5],
    figures: [],
    calc: (list, { calcParams }) => {
      const pivots = computePivots(list, calcParams[0] || 5, 'price');
      const res = list.map(() => ({}));
      pivots.forEach((p, idx) => {
        const prevSame = pivots.slice(0, idx).reverse().find(q => q.type === p.type);
        let tag = '';
        if (prevSame) {
          if (p.type === 'L') tag = p.price > prevSame.price ? 'HL' : 'LL'; // higher/lower low
          else tag = p.price < prevSame.price ? 'LH' : 'HH'; // lower/higher high
        }
        res[p.i] = { pivot: p.price, type: p.type, tag };
      });
      return res;
    },
    draw: ({ ctx, indicator, xAxis, yAxis }) => {
      const r = indicator.result || [];
      const pts = [];
      for (let i = 0; i < r.length; i++) if (r[i] && r[i].pivot != null) pts.push({ i, ...r[i] });
      if (pts.length < 2) return true;
      ctx.save();
      ctx.strokeStyle = '#8b5cf6';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      pts.forEach((p, k) => {
        const x = xAxis.convertToPixel(p.i), y = yAxis.convertToPixel(p.pivot);
        if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.stroke();
      // Tag labels (HL/LH/…) coloured by bullish/bearish structure.
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      for (const p of pts) {
        if (!p.tag) continue;
        const x = xAxis.convertToPixel(p.i), y = yAxis.convertToPixel(p.pivot);
        ctx.fillStyle = (p.tag === 'HL' || p.tag === 'HH') ? '#10b981' : '#ef4444';
        ctx.fillText(p.tag, x, p.type === 'H' ? y - 6 : y + 14);
      }
      ctx.restore();
      return true;
    },
  });
  zigzagRegistered = true;
}

// Wilder RSI(14) over a close series.
function computeRSI(closes, period = 14) {
  const n = closes.length;
  const rsi = new Array(n).fill(null);
  if (n < period + 1) return rsi;
  let gain = 0, loss = 0;
  for (let i = 1; i <= period; i++) { const ch = closes[i] - closes[i - 1]; if (ch > 0) gain += ch; else loss -= ch; }
  let avgG = gain / period, avgL = loss / period;
  rsi[period] = avgL === 0 ? 100 : 100 - 100 / (1 + avgG / avgL);
  for (let i = period + 1; i < n; i++) {
    const ch = closes[i] - closes[i - 1];
    avgG = (avgG * (period - 1) + (ch > 0 ? ch : 0)) / period;
    avgL = (avgL * (period - 1) + (ch < 0 ? -ch : 0)) / period;
    rsi[i] = avgL === 0 ? 100 : 100 - 100 / (1 + avgG / avgL);
  }
  return rsi;
}

// ── RSI divergence: dashed line between the two price pivots where price and
// RSI disagree (bullish: lower low / higher RSI low; bearish: higher high /
// lower RSI high). Drawn on the price pane. Pair with the RSI sub-pane.
let rsiDivRegistered = false;
function ensureRsiDiv() {
  if (rsiDivRegistered) return;
  registerIndicator({
    name: 'RSIDIV', shortName: 'Phân kỳ RSI', calcParams: [5, 14], figures: [],
    calc: (list, { calcParams }) => {
      const [lb, rp] = calcParams;
      const rsi = computeRSI(list.map(d => d.close), rp);
      const pivots = computePivots(list, lb, 'price');
      const res = list.map(() => ({}));
      const lows = pivots.filter(p => p.type === 'L');
      const highs = pivots.filter(p => p.type === 'H');
      for (let k = 1; k < lows.length; k++) {
        const a = lows[k - 1], b = lows[k];
        if (rsi[a.i] == null || rsi[b.i] == null) continue;
        if (b.price < a.price && rsi[b.i] > rsi[a.i]) res[b.i] = { div: 'bull', ai: a.i, ap: a.price, bi: b.i, bp: b.price };
      }
      for (let k = 1; k < highs.length; k++) {
        const a = highs[k - 1], b = highs[k];
        if (rsi[a.i] == null || rsi[b.i] == null) continue;
        if (b.price > a.price && rsi[b.i] < rsi[a.i]) res[b.i] = { div: 'bear', ai: a.i, ap: a.price, bi: b.i, bp: b.price };
      }
      return res;
    },
    draw: ({ ctx, indicator, xAxis, yAxis }) => {
      const r = indicator.result || [];
      ctx.save(); ctx.font = '10px sans-serif'; ctx.textAlign = 'center'; ctx.lineWidth = 1.5;
      for (let i = 0; i < r.length; i++) {
        const m = r[i]; if (!m || !m.div) continue;
        const x1 = xAxis.convertToPixel(m.ai), y1 = yAxis.convertToPixel(m.ap);
        const x2 = xAxis.convertToPixel(m.bi), y2 = yAxis.convertToPixel(m.bp);
        ctx.strokeStyle = m.div === 'bull' ? '#10b981' : '#ef4444';
        ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = ctx.strokeStyle;
        ctx.fillText(m.div === 'bull' ? 'Phân kỳ tăng' : 'Phân kỳ giảm', (x1 + x2) / 2, m.div === 'bull' ? Math.max(y1, y2) + 14 : Math.min(y1, y2) - 6);
      }
      ctx.restore(); return true;
    },
  });
  rsiDivRegistered = true;
}

// ── Chart patterns (heuristic, low confidence): head-and-shoulders, inverse
// H&S, and triangles, from the recent swing pivots. Labelled "(thăm dò)".
let patternsRegistered = false;
function ensurePatterns() {
  if (patternsRegistered) return;
  const near = (a, b, tol = 0.04) => Math.abs(a - b) / ((a + b) / 2) < tol;
  registerIndicator({
    name: 'PATTERNS', shortName: 'Mô hình', calcParams: [5], figures: [],
    calc: (list, { calcParams }) => {
      const pv = computePivots(list, calcParams[0] || 5, 'price');
      const res = list.map(() => ({}));
      const shapes = [];
      // Head & shoulders (top): H,L,H,L,H with middle highest, shoulders similar.
      for (let k = 0; k + 4 < pv.length; k++) {
        const w = pv.slice(k, k + 5);
        const t = w.map(p => p.type).join('');
        if (t === 'HLHLH') {
          const [s1, n1, head, n2, s2] = w;
          if (head.price > s1.price && head.price > s2.price && near(s1.price, s2.price) && near(n1.price, n2.price, 0.06))
            shapes.push({ kind: 'hns', label: 'Vai-đầu-vai (thăm dò)', pts: w, color: '#ef4444' });
        }
        if (t === 'LHLHL') {
          const [s1, n1, head, n2, s2] = w;
          if (head.price < s1.price && head.price < s2.price && near(s1.price, s2.price) && near(n1.price, n2.price, 0.06))
            shapes.push({ kind: 'ihns', label: 'Vai-đầu-vai ngược (thăm dò)', pts: w, color: '#10b981' });
        }
      }
      // Triangle: last 4 pivots with converging highs/lows.
      if (pv.length >= 4) {
        const w = pv.slice(-4);
        const highs = w.filter(p => p.type === 'H');
        const lows = w.filter(p => p.type === 'L');
        if (highs.length === 2 && lows.length === 2) {
          const descHigh = highs[1].price < highs[0].price;
          const ascLow = lows[1].price > lows[0].price;
          if (descHigh && ascLow) shapes.push({ kind: 'tri', label: 'Tam giác (thăm dò)', pts: w, color: '#f59e0b' });
        }
      }
      // Keep only the most recent of each kind; store on last pivot index.
      const last = shapes[shapes.length - 1];
      if (last) res[last.pts[last.pts.length - 1].i] = { shape: last };
      return res;
    },
    draw: ({ ctx, indicator, xAxis, yAxis }) => {
      const r = indicator.result || [];
      ctx.save(); ctx.font = 'bold 10px sans-serif'; ctx.textAlign = 'center'; ctx.lineWidth = 1.5;
      for (let i = 0; i < r.length; i++) {
        const s = r[i] && r[i].shape; if (!s) continue;
        ctx.strokeStyle = s.color; ctx.setLineDash([3, 3]);
        ctx.beginPath();
        s.pts.forEach((p, k) => { const x = xAxis.convertToPixel(p.i), y = yAxis.convertToPixel(p.price); if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); });
        ctx.stroke(); ctx.setLineDash([]);
        const mid = s.pts[Math.floor(s.pts.length / 2)];
        ctx.fillStyle = s.color;
        ctx.fillText(s.label, xAxis.convertToPixel(mid.i), yAxis.convertToPixel(mid.price) - 10);
      }
      ctx.restore(); return true;
    },
  });
  patternsRegistered = true;
}

// Editable auto trend lines through the two most recent swing lows (support)
// and swing highs (resistance). Drawn as a rayLine anchored at the OLDER pivot
// and passing through the newer one, so the line extends forward to the latest
// candle — showing whether price still holds the trend or has broken it. The
// two control points stay draggable.
function autoTrendOverlays(candles) {
  const pv = computePivots(candles, 5, 'price');
  const lows = pv.filter(p => p.type === 'L').slice(-2);
  const highs = pv.filter(p => p.type === 'H').slice(-2);
  // a = older pivot (ray start), b = newer pivot (ray direction) → extends right.
  const ray = (a, b, color) => ({
    name: 'rayLine', groupId: 'auto-trend',
    points: [
      { timestamp: candles[a.i].timestamp, value: a.price },
      { timestamp: candles[b.i].timestamp, value: b.price },
    ],
    styles: { line: { color, size: 1 } },
  });
  const out = [];
  if (lows.length === 2) out.push(ray(lows[0], lows[1], '#10b981'));
  if (highs.length === 2) out.push(ray(highs[0], highs[1], '#ef4444'));
  return out;
}

// ── Custom Ichimoku indicator (KLineCharts has no built-in) ─────────────────
// Tenkan(9)/Kijun(26)/Span A/Span B(52). Spans are computed from data `kijun`
// bars back and placed at the current index, matching how the analysis feature
// compares price against the cloud (no future projection needed).
let ichimokuRegistered = false;
function ensureIchimoku() {
  if (ichimokuRegistered) return;
  const midHigh = (list, period, i) => {
    if (i - period + 1 < 0) return null;
    let m = -Infinity;
    for (let j = i - period + 1; j <= i; j++) m = Math.max(m, list[j].high);
    return m;
  };
  const midLow = (list, period, i) => {
    if (i - period + 1 < 0) return null;
    let m = Infinity;
    for (let j = i - period + 1; j <= i; j++) m = Math.min(m, list[j].low);
    return m;
  };
  registerIndicator({
    name: 'ICHIMOKU',
    shortName: 'Ichimoku',
    calcParams: [9, 26, 52],
    figures: [
      // Colour per figure. NOTE: an indicator-level `styles: { lines: [...] }`
      // override throws inside klinecharts during render ("reading '0'"), which
      // aborts the whole candle-pane draw every frame — that's what froze the
      // candles while the axis/overlays/volume kept updating. Per-figure styles
      // avoid it. (Verified in a browser repro.)
      { key: 'tenkan', title: 'Tenkan: ', type: 'line', styles: () => ({ color: '#2962FF' }) },
      { key: 'kijun', title: 'Kijun: ', type: 'line', styles: () => ({ color: '#B71C1C' }) },
      { key: 'spanA', title: 'Span A: ', type: 'line', styles: () => ({ color: '#26A69A' }) },
      { key: 'spanB', title: 'Span B: ', type: 'line', styles: () => ({ color: '#EF5350' }) },
    ],
    calc: (list, { calcParams }) => {
      const [tp, kp, sp] = calcParams;
      const n = list.length;
      const tenkan = new Array(n).fill(null);
      const kijun = new Array(n).fill(null);
      const spanARaw = new Array(n).fill(null);
      const spanBRaw = new Array(n).fill(null);
      for (let i = 0; i < n; i++) {
        const th = midHigh(list, tp, i), tl = midLow(list, tp, i);
        if (th != null && tl != null) tenkan[i] = (th + tl) / 2;
        const kh = midHigh(list, kp, i), kl = midLow(list, kp, i);
        if (kh != null && kl != null) kijun[i] = (kh + kl) / 2;
        if (tenkan[i] != null && kijun[i] != null) spanARaw[i] = (tenkan[i] + kijun[i]) / 2;
        const bh = midHigh(list, sp, i), bl = midLow(list, sp, i);
        if (bh != null && bl != null) spanBRaw[i] = (bh + bl) / 2;
      }
      return list.map((_, i) => ({
        tenkan: tenkan[i] ?? undefined,
        kijun: kijun[i] ?? undefined,
        spanA: i >= kp ? (spanARaw[i - kp] ?? undefined) : undefined,
        spanB: i >= kp ? (spanBRaw[i - kp] ?? undefined) : undefined,
      }));
    },
    // Kumo (cloud): fill between Span A and Span B, drawn behind the lines.
    // Green when Span A ≥ Span B (bullish), red otherwise.
    draw: ({ ctx, indicator, visibleRange, xAxis, yAxis }) => {
      const r = indicator.result || [];
      const { from, to } = visibleRange;
      ctx.save();
      let prev = null;
      for (let i = from; i < to; i++) {
        const d = r[i];
        if (!d || d.spanA == null || d.spanB == null) { prev = null; continue; }
        const x = xAxis.convertToPixel(i);
        const yA = yAxis.convertToPixel(d.spanA);
        const yB = yAxis.convertToPixel(d.spanB);
        if (prev) {
          ctx.beginPath();
          ctx.moveTo(prev.x, prev.yA);
          ctx.lineTo(x, yA);
          ctx.lineTo(x, yB);
          ctx.lineTo(prev.x, prev.yB);
          ctx.closePath();
          ctx.fillStyle = d.spanA >= d.spanB ? 'rgba(38,166,154,0.18)' : 'rgba(239,83,80,0.18)';
          ctx.fill();
        }
        prev = { x, yA, yB };
      }
      ctx.restore();
      return false; // continue drawing the four lines on top
    },
  });
  ichimokuRegistered = true;
}


// Interactive drawing tools (KLineCharts built-in overlays).
const DRAW_TOOLS = [
  { name: 'segment', label: 'Trend line' },
  { name: 'rayLine', label: 'Tia' },
  { name: 'parallelStraightLine', label: 'Kênh song song' },
  { name: 'priceChannelLine', label: 'Kênh giá' },
  { name: 'polygon', label: 'Tam giác/Đa giác' },
  { name: 'fibonacciLine', label: 'Fibonacci' },
  { name: 'horizontalStraightLine', label: 'Ngang' },
  { name: 'verticalStraightLine', label: 'Dọc' },
];

const SOURCE_LABEL = { binance: 'Binance', bybit: 'Bybit' };

// Horizontal reference levels drawn from the analysis document. Each is a
// full-width line; the legend below the chart names them.
function levelLines(k) {
  if (!k) return [];
  const L = (group, value, color, label, style = 'dashed') =>
    (Number.isFinite(value) ? { group, value, color, label, style } : null);
  return [
    L('sr', k.monthResistance, '#ef5350', 'Kháng cự tháng'),
    L('sr', k.monthSupport, '#26a69a', 'Hỗ trợ tháng'),
    L('sr', k.swingHigh, '#9333ea', 'Đỉnh swing', 'dotted'),
    L('sr', k.swingLow, '#0891b2', 'Đáy swing', 'dotted'),
    L('fib', k.fib618, '#f59e0b', 'Fib 61.8%', 'dotted'),
    L('fib', k.fib500, '#eab308', 'Fib 50%', 'dotted'),
    L('fib', k.fib382, '#22c55e', 'Fib 38.2%', 'dotted'),
  ].filter(Boolean);
}

// Toggle groups shown in the UI. `ind` = candle-pane indicator name (built-in
// or custom); overlay groups (fib/sr) and volume are handled specially.
const TOGGLE_GROUPS = [
  { key: 'ema', label: 'EMA 50/200', ind: 'EMA', create: (c) => c.createIndicator({ name: 'EMA', calcParams: [50, 200] }, true, { id: 'candle_pane' }) },
  { key: 'bb', label: 'Bollinger', ind: 'BOLL', create: (c) => c.createIndicator({ name: 'BOLL', calcParams: [20, 2] }, true, { id: 'candle_pane' }) },
  { key: 'ichimoku', label: 'Ichimoku', ind: 'ICHIMOKU', create: (c) => c.createIndicator('ICHIMOKU', true, { id: 'candle_pane' }) },
  { key: 'zigzag', label: 'Zigzag đỉnh/đáy', ind: 'ZIGZAG', create: (c) => c.createIndicator({ name: 'ZIGZAG', calcParams: [5] }, true, { id: 'candle_pane' }) },
  { key: 'trendline', label: 'Trend line (tự vẽ)' },
  { key: 'patterns', label: 'Mô hình', ind: 'PATTERNS', create: (c) => c.createIndicator({ name: 'PATTERNS', calcParams: [5] }, true, { id: 'candle_pane' }) },
  { key: 'rsidiv', label: 'Phân kỳ RSI' },
  { key: 'fib', label: 'Fibonacci' },
  { key: 'sr', label: 'Hỗ trợ/Kháng cự' },
  { key: 'vol', label: 'Volume' },
];

// Reconcile the chart's indicators/overlays with the desired visibility, using
// `created` (a mutable ref) to track what currently exists — so toggling only
// adds/removes the delta and never touches the candle data or zoom.
function applyVisibility(chart, vis, lines, candles, created) {
  if (!chart) return;
  // Simple candle-pane indicators (EMA / BOLL / ICHIMOKU / ZIGZAG / PATTERNS)
  for (const g of TOGGLE_GROUPS) {
    if (!g.ind) continue;
    if (vis[g.key] && !created[g.key]) { g.create(chart); created[g.key] = true; }
    else if (!vis[g.key] && created[g.key]) { chart.removeIndicator('candle_pane', g.ind); created[g.key] = false; }
  }
  // Volume (its own pane)
  if (vis.vol && !created.vol) { created.volPaneId = chart.createIndicator('VOL'); created.vol = true; }
  else if (!vis.vol && created.vol) { if (created.volPaneId) chart.removeIndicator(created.volPaneId, 'VOL'); created.vol = false; created.volPaneId = null; }
  // RSI divergence = RSI sub-pane + the divergence overlay on the price pane.
  if (vis.rsidiv && !created.rsidiv) {
    created.rsiPaneId = chart.createIndicator({ name: 'RSI', calcParams: [14] });
    chart.createIndicator({ name: 'RSIDIV', calcParams: [5, 14] }, true, { id: 'candle_pane' });
    created.rsidiv = true;
  } else if (!vis.rsidiv && created.rsidiv) {
    if (created.rsiPaneId) chart.removeIndicator(created.rsiPaneId, 'RSI');
    chart.removeIndicator('candle_pane', 'RSIDIV');
    created.rsidiv = false; created.rsiPaneId = null;
  }
  // Locked reference-level overlays (Fibonacci / Support-Resistance).
  for (const key of ['fib', 'sr']) {
    if (vis[key] && !created[key]) {
      created[`${key}Ids`] = lines.filter(l => l.group === key).map(ln => {
        try {
          return chart.createOverlay({
            name: 'priceLine', lock: true, points: [{ value: ln.value }],
            styles: { line: { color: ln.color, style: ln.style, size: 1 } },
          });
        } catch { return null; }
      }).filter(Boolean);
      created[key] = true;
    } else if (!vis[key] && created[key]) {
      (created[`${key}Ids`] || []).forEach(id => chart.removeOverlay(id));
      created[`${key}Ids`] = [];
      created[key] = false;
    }
  }
  // Auto trend lines — EDITABLE segment overlays (user can drag). Recreated
  // from the pivots each time the group is turned on; skipped if none found.
  if (vis.trendline && !created.trendline) {
    created.trendIds = (candles && candles.length ? autoTrendOverlays(candles) : []).map(o => {
      try { return chart.createOverlay(o); } catch { return null; }
    }).filter(Boolean);
    created.trendline = true;
  } else if (!vis.trendline && created.trendline) {
    chart.removeOverlay({ groupId: 'auto-trend' });
    created.trendIds = [];
    created.trendline = false;
  }
}

// Read a CSS variable off the page so the canvas chart follows the app theme.
function cssVar(name, fallback) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function buildStyles() {
  const text = cssVar('--text-muted', '#64748b');
  const grid = cssVar('--border', 'rgba(148,163,184,0.2)');
  return {
    grid: { horizontal: { color: grid }, vertical: { color: grid } },
    candle: {
      bar: {
        upColor: '#26A69A', downColor: '#EF5350', noChangeColor: '#888888',
        upBorderColor: '#26A69A', downBorderColor: '#EF5350',
        upWickColor: '#26A69A', downWickColor: '#EF5350',
      },
      priceMark: { last: { text: { color: '#ffffff' } } },
      tooltip: { text: { color: text } },
    },
    xAxis: { axisLine: { color: grid }, tickLine: { color: grid }, tickText: { color: text } },
    yAxis: { axisLine: { color: grid }, tickLine: { color: grid }, tickText: { color: text } },
    crosshair: { horizontal: { text: { color: '#ffffff' } }, vertical: { text: { color: '#ffffff' } } },
    indicator: { tooltip: { text: { color: text } } },
  };
}

export default function TechnicalChart({ symbol, keyLevels }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [source, setSource] = useState('binance');
  // Which indicator groups are shown. Toggling updates the chart in place.
  // Auto-drawn groups (trendline, patterns) default ON; heavier analytical
  // ones (zigzag, RSI divergence) default OFF. All hide/show like indicators.
  const [vis, setVis] = useState({
    ema: true, bb: true, ichimoku: true, zigzag: false, trendline: true,
    patterns: true, rsidiv: false, fib: true, sr: true, vol: true,
  });

  const lines = levelLines(keyLevels);
  // Refs so the (symbol-keyed) build effect always sees the latest values
  // without re-running when they change.
  const visRef = useRef(vis); visRef.current = vis;
  const linesRef = useRef(lines); linesRef.current = lines;
  const candlesRef = useRef([]); // latest fetched candles (for auto trend lines)
  // Tracks what is currently created on the chart so toggles apply only a delta.
  const createdRef = useRef({});

  useEffect(() => {
    if (!symbol || !containerRef.current) return undefined;
    let disposed = false;
    ensureIchimoku();
    ensureZigzag();
    ensureRsiDiv();
    ensurePatterns();

    const chart = init(containerRef.current, { styles: buildStyles() });
    chartRef.current = chart;
    createdRef.current = {}; // fresh chart — nothing created yet

    setStatus('loading');
    (async () => {
      try {
        const { candles, source: src } = await getTechnicalKlines(symbol);
        if (disposed) return;
        setSource(src);
        candlesRef.current = candles;
        chart.applyNewData(candles);
        applyVisibility(chart, visRef.current, linesRef.current, candles, createdRef.current);

        // The container often mounts narrow (inside the flex layout) and then
        // widens; without a resize the pane boundings stay stale and wheel
        // hit-testing on the candle pane can miss. Resize once layout settles.
        requestAnimationFrame(() => chartRef.current && chartRef.current.resize());
        if (!disposed) setStatus('ready');
      } catch {
        if (!disposed) setStatus('error');
      }
    })();

    // Keep pane boundings correct when the container resizes (sidebar toggle,
    // window resize, panel expand) so zoom/scroll hit-testing stays accurate.
    const ro = new ResizeObserver(() => chartRef.current && chartRef.current.resize());
    ro.observe(containerRef.current);

    return () => {
      disposed = true;
      ro.disconnect();
      dispose(containerRef.current);
      chartRef.current = null;
      createdRef.current = {};
    };
  }, [symbol]);

  // Apply show/hide when the toggles change (chart already built).
  useEffect(() => {
    if (chartRef.current) applyVisibility(chartRef.current, vis, linesRef.current, candlesRef.current, createdRef.current);
  }, [vis]);

  const toggle = (key) => setVis(v => ({ ...v, [key]: !v[key] }));
  const visibleLines = lines.filter(ln => vis[ln.group]);

  // Start an interactive drawing (segment/channel/triangle/fib/…). All manual
  // drawings share groupId 'draw' so "Xóa vẽ" can clear just those.
  const draw = (toolName) => {
    if (chartRef.current) chartRef.current.createOverlay({ name: toolName, groupId: 'draw' });
  };
  const clearDrawings = () => {
    if (chartRef.current) chartRef.current.removeOverlay({ groupId: 'draw' });
  };

  return (
    <div style={s.wrap}>
      <div style={s.head}>
        <span style={s.title}>Biểu đồ kỹ thuật 1D — {symbol} <span style={s.hint}>({SOURCE_LABEL[source] || source})</span></span>
        <span style={s.hint}>Bật/tắt chỉ báo để bớt rối mắt</span>
      </div>
      <div style={s.toggles}>
        {TOGGLE_GROUPS.map(g => (
          <button
            key={g.key}
            type="button"
            style={{ ...s.toggleBtn, ...(vis[g.key] ? s.toggleOn : s.toggleOff) }}
            onClick={() => toggle(g.key)}
            title={vis[g.key] ? 'Đang hiện — bấm để ẩn' : 'Đang ẩn — bấm để hiện'}
          >
            {vis[g.key] ? '☑' : '☐'} {g.label}
          </button>
        ))}
      </div>
      <div style={s.drawBar}>
        <span style={s.drawLabel}>✏️ Vẽ:</span>
        {DRAW_TOOLS.map(t => (
          <button key={t.name} type="button" style={s.drawBtn} onClick={() => draw(t.name)}>{t.label}</button>
        ))}
        <button type="button" style={{ ...s.drawBtn, ...s.clearBtn }} onClick={clearDrawings}>🗑 Xóa vẽ</button>
      </div>
      <div ref={containerRef} style={s.chart} />
      {status === 'loading' && <div style={s.msg}>Đang tải biểu đồ...</div>}
      {status === 'error' && <div style={s.msg}>Không tải được dữ liệu nến từ {SOURCE_LABEL[source] || source}.</div>}
      {status === 'ready' && visibleLines.length > 0 && (
        <div style={s.legend}>
          {visibleLines.map(ln => (
            <span key={ln.label} style={s.legendItem}>
              <span style={{ ...s.swatch, background: ln.color }} /> {ln.label}: ${fmtLevel(ln.value)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtLevel(n) {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (n >= 1) return n.toFixed(2);
  return n.toPrecision(4);
}

const s = {
  wrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 14, marginBottom: 16 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 10 },
  title: { fontSize: 13, fontWeight: 800, color: 'var(--text)' },
  hint: { fontSize: 10, color: 'var(--text-dim)' },
  toggles: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 },
  toggleBtn: {
    fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 7,
    cursor: 'pointer', border: '1px solid var(--border)', whiteSpace: 'nowrap',
  },
  toggleOn: { background: 'var(--bg)', color: 'var(--text)', borderColor: 'var(--accent)' },
  toggleOff: { background: 'var(--surface)', color: 'var(--text-dim)' },
  drawBar: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 10 },
  drawLabel: { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' },
  drawBtn: {
    fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 7, cursor: 'pointer',
    border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-muted)', whiteSpace: 'nowrap',
  },
  clearBtn: { color: '#ef4444', borderColor: 'rgba(239,68,68,0.35)' },
  chart: { width: '100%', height: 420 },
  msg: { textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: 16 },
  legend: { display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10, fontSize: 11, color: 'var(--text-muted)' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 5, fontVariantNumeric: 'tabular-nums' },
  swatch: { width: 12, height: 3, borderRadius: 2, display: 'inline-block' },
};
